import {
  type FC, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  EntityType, SharedEntityType,
} from '~/_shared/entityAccess/entityAccess.repository';
import { useManageEntityAccessInvitations } from '~/_shared/entityAccess/entityAccessInvitations.hooks';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullOrUndefined } from '~/_shared/utils/typeGuards';
import { type Member } from '~/clientTeamManagement/teamManagementModal/teamManagement.types';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { AppErrorType } from '../../../appError/appErrorType.enum';
import { MemberStatus } from '../../teamManagementModal/teamManagement.repository';
import { teamShareMapPermissionToEntityAccessLevel } from '../teamShareMap.helpers';
import { type TeamShareMapPermission } from '../teamShareMap.types';
import { type MapAccessData } from '../teamShareMapModal.container';
import { NewMemberFormComponent } from './newMemberForm.component';

export type NewMemberFormContainerProps = Readonly<{
  mapAccessData: MapAccessData[];
  mapId: number;
  onNewMemberAdded?: () => void;
}>;

export const NewMemberFormContainer: FC<NewMemberFormContainerProps> = (props) => {
  const { mapId, onNewMemberAdded, mapAccessData } = props;
  const clientId = useClientIdSelector();
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const { createEntityAccessInvitation, isLoading } = useManageEntityAccessInvitations();

  const onAddUsers = useCallback((
    newMembers: Member[],
    permissionLevel: TeamShareMapPermission,
    onSuccess?: () => void,
  ) => {
    if (!clientId) {
      return;
    }

    createEntityAccessInvitation(
      newMembers.map((item) => {
        if (!item?.id) {
          return null;
        }
        return {
          entityType: EntityType.MAP,
          sharedEntityType: SharedEntityType.USER,
          accessLevel: teamShareMapPermissionToEntityAccessLevel(permissionLevel),
          entityId: mapId,
          sharedEntityId: item.id,
        };
      }).filter(notNullOrUndefined)
    ).then(() => {
      onNewMemberAdded?.();
      onSuccess?.();
    }).catch(() => {
      dispatch(
        createAppError({
          type: AppErrorType.General,
          title: t('Error'),
          content: t('teamShareMap.addingUserError'),
          errorTitle: t('teamShareMap.addingUserErrorTitle'),
        })
      );
    });
  }, [clientId, createEntityAccessInvitation, dispatch, mapId, onNewMemberAdded, t]);

  const excludeExistingMembersFilter = useCallback((item: Member) => (
    !!item.id &&
    item.status === MemberStatus.ENABLED &&
    !mapAccessData
      .map((item) => item.entityAccess?.sharedEntityId.toString() || item.invitation?.sharedEntityId.toString())
      .filter(notNullOrUndefined)
      .includes(item.id.toString())
  ), [mapAccessData]);

  return (
    <NewMemberFormComponent
      onAddUsers={onAddUsers}
      excludeExistingMembersFilter={excludeExistingMembersFilter}
      isLoading={isLoading}
    />
  );
};
